import { useCallback } from 'react';
import { toCanvas } from 'html-to-image';
import * as Sentry from '@sentry/react';

/**
 * Hook that captures a screenshot of the current viewport for feedback reports.
 *
 * html-to-image renders cloned DOM inside an SVG `<foreignObject>` which the
 * browser's SVG-to-image pipeline cannot draw `position: fixed` descendants
 * for. That drops every overlay (Modal, Drawer, toast) from the output even
 * though they're present in the live DOM. We work around that by capturing
 * the body subtree once for the underlying page, then capturing each
 * top-level fixed-positioned element as its own root (where it isn't fixed
 * from the capture's point of view) and compositing those onto the base
 * canvas at their on-screen bounding rect.
 */
export function useFeedbackScreenshot() {
  const capture = useCallback(async (): Promise<string | null> => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const devicePixelRatio = window.devicePixelRatio;
    try {
      await waitForVisualFrame();
      const bgColor = getComputedStyle(document.body).backgroundColor || '#ffffff';

      const baseCanvas = await toCanvas(document.body, {
        width: viewportWidth,
        height: viewportHeight,
        canvasWidth: viewportWidth,
        canvasHeight: viewportHeight,
        pixelRatio: 1,
        backgroundColor: bgColor,
      });

      const overlays = findTopLevelFixedElements();
      const ctx = baseCanvas.getContext('2d');
      let composited = 0;
      let skipped = 0;
      for (const el of overlays) {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) continue;
        if (
          rect.right <= 0 ||
          rect.bottom <= 0 ||
          rect.left >= viewportWidth ||
          rect.top >= viewportHeight
        ) {
          continue;
        }
        try {
          const overlayCanvas = await toCanvas(el, { pixelRatio: 1 });
          ctx?.drawImage(overlayCanvas, rect.left, rect.top, rect.width, rect.height);
          composited += 1;
        } catch (overlayErr) {
          skipped += 1;
          Sentry.addBreadcrumb({
            category: 'feedback',
            message: 'failed to capture overlay; skipping',
            level: 'warning',
            data: {
              className: typeof el.className === 'string' ? el.className.slice(0, 80) : null,
              tag: el.tagName,
              error: overlayErr instanceof Error ? overlayErr.message : String(overlayErr),
            },
          });
        }
      }

      const dataUrl = baseCanvas.toDataURL('image/png');
      Sentry.addBreadcrumb({
        category: 'feedback',
        message: 'feedback screenshot captured',
        level: 'info',
        data: {
          sizeBytes: dataUrl.length,
          viewportHeight,
          viewportWidth,
          devicePixelRatio,
          overlaysComposited: composited,
          overlaysSkipped: skipped,
        },
      });
      return dataUrl;
    } catch (err) {
      if (isCanvasExportBlocked(err)) {
        Sentry.addBreadcrumb({
          category: 'feedback',
          message: 'feedback screenshot skipped because canvas export was blocked',
          level: 'warning',
          data: {
            error: err instanceof Error ? err.message : String(err),
          },
        });
        return null;
      }

      Sentry.withScope((scope) => {
        scope.setTag('feature', 'bug-report');
        scope.setTag('phase', 'capture-screenshot');
        scope.setContext('viewport', {
          viewportHeight,
          viewportWidth,
          devicePixelRatio,
        });
        Sentry.captureException(err);
      });
      return null;
    }
  }, []);

  return { capture };
}

function isCanvasExportBlocked(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'SecurityError') return true;
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'SecurityError' ||
    error.message.includes('Tainted canvases may not be exported')
  );
}

/**
 * Returns every position:fixed element that isn't nested inside another
 * fixed element. Nested fixed children come along for the ride when their
 * top-level ancestor is captured.
 */
function findTopLevelFixedElements(): HTMLElement[] {
  const allFixed = Array.from(document.querySelectorAll<HTMLElement>('body *')).filter(
    (el) => getComputedStyle(el).position === 'fixed'
  );
  const fixedSet = new Set<HTMLElement>(allFixed);
  return allFixed.filter((el) => {
    let parent = el.parentElement;
    while (parent) {
      if (fixedSet.has(parent)) return false;
      parent = parent.parentElement;
    }
    return true;
  });
}

function waitForVisualFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => resolve());
      });
      return;
    }
    window.setTimeout(resolve, 0);
  });
}
